import React, { useEffect, useState } from 'react';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  AlertCircle,
  Shield,
  Building2,
  Users,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  isPlatformAuthenticated,
  platformLogin,
  storePlatformSession,
} from '../../utils/platformAuth';
import { ApiValidationError } from '../../types/auth';
import type { LoginCredentials } from '../../types/platformAdmin';

interface ValidationErrors {
  email?: string[];
  password?: string[];
  [key: string]: string[] | undefined;
}

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [formData, setFormData] = useState<LoginCredentials>({
    email: '',
    password: '',
  });

  useEffect(() => {
    if (isPlatformAuthenticated()) {
      navigate('/platform-admin/dashboard', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setValidationErrors({});

    try {
      const { token, user } = await platformLogin(formData);
      storePlatformSession(token, user);
      navigate('/platform-admin/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setValidationErrors(err.errors);
        const firstFieldError = Object.values(err.errors).flat()[0];
        setError(firstFieldError || err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An error occurred during login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const inputClass = (field: string) =>
    `w-full pl-10 pr-4 py-2.5 text-sm border rounded-lg bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 transition-all ${
      validationErrors[field] ? 'border-red-300' : 'border-slate-200 hover:border-slate-300'
    }`;

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-[45%] xl:w-1/2 relative bg-indigo-950 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-indigo-950 to-slate-950" />
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '32px 32px',
          }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center shadow-lg">
                <Building2 className="h-6 w-6 text-indigo-800" />
              </div>
              <span className="text-xl font-bold text-white tracking-tight leading-tight">
                briX Platform Admin
              </span>
            </div>
          </div>

          <div className="space-y-8">
            <div>
              <h2 className="text-3xl xl:text-4xl font-bold text-white leading-tight tracking-tight">
                Manage tenant<br />
                companies at scale.
              </h2>
              <p className="mt-4 text-indigo-200/80 text-lg leading-relaxed max-w-md">
                Provision new companies, manage tenant access, and oversee platform operations from a
                single console.
              </p>
            </div>

            <div className="space-y-4">
              {[
                { icon: Building2, text: 'Create and configure tenant companies' },
                { icon: Users, text: 'Bootstrap admin users and chart of accounts' },
                { icon: Shield, text: 'Platform-level access control' },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-indigo-900/80 border border-indigo-700 flex items-center justify-center">
                    <Icon className="h-4 w-4 text-indigo-200" />
                  </div>
                  <span className="text-indigo-100 text-sm">{text}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="text-indigo-400/70 text-sm">
            &copy; {new Date().getFullYear()} briX Platform. All rights reserved.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center bg-slate-50 px-6 py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center gap-3 mb-10">
            <div className="w-10 h-10 bg-indigo-700 rounded-xl flex items-center justify-center shadow-md">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-bold text-slate-900 tracking-tight leading-tight">
              briX Platform Admin
            </span>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm shadow-slate-200/50 p-8 sm:p-10">
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Platform sign in</h1>
              <p className="mt-2 text-sm text-slate-500">
                Sign in with your platform administrator credentials
              </p>
            </div>

            {error && (
              <div className="mb-6 flex items-start gap-3 p-4 bg-red-50 border border-red-100 text-red-700 rounded-lg text-sm">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Email address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-4 w-4 text-slate-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    className={inputClass('email')}
                    placeholder="platform@brix.local"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                {validationErrors.email && (
                  <p className="mt-1.5 text-sm text-red-600">{validationErrors.email[0]}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-4 w-4 text-slate-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    className={`${inputClass('password')} pr-10`}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {validationErrors.password && (
                  <p className="mt-1.5 text-sm text-red-600">{validationErrors.password[0]}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className={`w-full flex justify-center items-center gap-2 py-2.5 px-4 rounded-lg text-sm font-semibold text-white bg-indigo-700 hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-600 transition-all ${
                  isLoading ? 'opacity-70 cursor-not-allowed' : 'shadow-sm hover:shadow'
                }`}
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign in
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          </div>

          <p className="mt-8 text-center text-sm text-slate-500">
            Tenant user?{' '}
            <a href="/signin" className="font-medium text-indigo-700 hover:text-indigo-600">
              Go to accounting sign in
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
